// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isDefined } from '@shared/utils';
import type { TFunction } from 'i18next';
import { getBankUploadExcelFieldLabel } from './bank-upload-excel-field-label';
import type { BankUploadParseError } from './types';

/** Builds a single user-facing line for an Excel import error (locale via `t`). */
export const formatBankUploadParseError = (
  error: BankUploadParseError,
  t: TFunction,
): string => {
  if (error.code !== 'row_invalid') {
    return t(`aggregator.create-bank.excel.import.code.${error.code}`, {
      ns: 'aggregator',
      ...(error.values ?? {}),
      defaultValue: t('aggregator.create-bank.excel.import.code.generic', {
        ns: 'aggregator',
      }),
    });
  }

  const entity = error.entity;

  const fieldLabel = isDefined(entity)
    ? getBankUploadExcelFieldLabel(entity, error.field, t)
    : error.field;

  const issueKind = error.issueKind ?? 'invalid';

  const detail = t(`aggregator.create-bank.excel.import.issue.${issueKind}`, {
    ns: 'aggregator',
    field: fieldLabel,
    defaultValue: t('aggregator.create-bank.excel.import.issue.invalid', {
      ns: 'aggregator',
      field: fieldLabel,
    }),
  });

  if (isDefined(error.row)) {
    return t('aggregator.create-bank.excel.import.error-row', {
      ns: 'aggregator',
      row: error.row,
      message: detail,
    });
  }

  return detail;
};
